import { useState, useEffect, useCallback, useRef } from "react";
import { loadDictionary, lemmatizeText, isLoaded, setActiveDictionary } from "./lemmatizer";
import { loadDefinitions, getDefinitions, setActiveDefinitions } from "./definitions";
import { initDb, reviewWord, getWordsNeedingReview, markKnown, type Db } from "./db";
import { Rating, type Grade } from "./fsrs";
import { t, setLocale, locales } from "./i18n";
import { useLocale } from "./useLocale";
import { languagePairs, getDefaultPair, savePairChoice, pairId, type LanguagePair } from "./languages";
import "./index.css";

type Phase = "input" | "triage" | "studying" | "done"

function useDismissible(key: string): [boolean, () => void] {
  const storageKey = `llemma_tip_${key}`;
  const [visible, setVisible] = useState(() => !localStorage.getItem(storageKey));
  const dismiss = useCallback(() => {
    localStorage.setItem(storageKey, "1");
    setVisible(false);
  }, [storageKey]);
  return [visible, dismiss];
}

function Tip({ children, onDismiss }: { children: React.ReactNode; onDismiss: () => void }) {
  return (
    <div className="mb-4 p-4 rounded-r-lg border-l-2 border-amber-600/60 bg-amber-950/20 text-sm text-neutral-400 italic relative">
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 text-amber-500/70 hover:text-amber-400 transition-colors text-xs not-italic font-medium"
        aria-label="Dismiss"
      >
        {t("dismiss")}
      </button>
      <div className="pr-10">{children}</div>
    </div>
  );
}

const TRIAGE_BATCH_SIZE = 35

interface SessionCard {
  lemma: string
  flipped: boolean
}

function HamburgerMenu({ langPair, onLangPairChange }: { langPair: LanguagePair; onLangPairChange: (pair: LanguagePair) => void }) {
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/50 transition-colors"
        aria-label="Menu"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <line x1="3" y1="5" x2="17" y2="5" />
          <line x1="3" y1="10" x2="17" y2="10" />
          <line x1="3" y1="15" x2="17" y2="15" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 py-1 rounded-lg bg-neutral-800 border border-neutral-700 shadow-lg z-10 min-w-44">
          <p className="px-3 py-1.5 text-xs text-neutral-500 uppercase tracking-wider">{t("studyLanguage")}</p>
          {languagePairs.map(pair => (
            <button
              key={pairId(pair)}
              onClick={() => { onLangPairChange(pair); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                pairId(langPair) === pairId(pair)
                  ? "text-neutral-200 bg-neutral-700/50"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/30"
              }`}
            >
              {pair.label}
            </button>
          ))}
          <div className="my-1 border-t border-neutral-700" />
          <p className="px-3 py-1.5 text-xs text-neutral-500 uppercase tracking-wider">{t("language")}</p>
          {locales.map(({ code, label }) => (
            <button
              key={code}
              onClick={() => { setLocale(code); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                locale === code
                  ? "text-neutral-200 bg-neutral-700/50"
                  : "text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function App() {
  useLocale(); // trigger re-render on locale change
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("input");
  const [queue, setQueue] = useState<SessionCard[]>([]);
  const [totalCards, setTotalCards] = useState(0);
  const [reviewed, setReviewed] = useState(0);
  const [langPair, setLangPair] = useState<LanguagePair>(getDefaultPair);
  const sourceLang = langPair.source;

  const [triageLemmas, setTriageLemmas] = useState<string[]>([]);
  const [triagePage, setTriagePage] = useState(0);
  const [triageMarked, setTriageMarked] = useState<Map<string, "good" | "known">>(new Map());
  const [triageVisited, setTriageVisited] = useState<Set<number>>(new Set());
  const dbRef = useRef<Db | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  useEffect(() => {
    dbRef.current = initDb();
  }, []);

  // Load language data on demand when langPair changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    setActiveDictionary(langPair.lemmaFile);
    setActiveDefinitions(langPair.dictFile);
    Promise.all([loadDictionary(langPair.lemmaFile), loadDefinitions(langPair.dictFile)])
      .then(() => setLoading(false))
      .catch((e) => {
        setError(t("errorLoadingDictionary"));
        setLoading(false);
        console.error(e);
      });
  }, [langPair]);

  const changeLangPair = useCallback((pair: LanguagePair) => {
    savePairChoice(pair);
    setLangPair(pair);
  }, []);

  const startSession = useCallback(async () => {
    if (!dbRef.current || !isLoaded() || !text.trim()) return;

    const results = lemmatizeText(text, sourceLang);
    const uniqueLemmas = [...new Set(results.map(r => r.lemma))];
    const needReview = (await getWordsNeedingReview(dbRef.current, uniqueLemmas, 0.95, Date.now(), sourceLang))
      .filter(lemma => getDefinitions(lemma));

    if (needReview.length === 0) {
      setPhase("done");
      setTotalCards(0);
      return;
    }

    setTriageLemmas(needReview);
    setTriagePage(0);
    setTriageMarked(new Map());
    setTriageVisited(new Set([0]));
    setPhase("triage");
  }, [text, sourceLang]);

  const triageClick = useCallback((lemma: string) => {
    setTriageMarked(prev => {
      const next = new Map(prev);
      if (next.get(lemma) === "good") {
        next.delete(lemma);
      } else {
        next.set(lemma, "good");
      }
      return next;
    });
  }, []);

  const triageRightClick = useCallback((lemma: string, e: React.MouseEvent) => {
    e.preventDefault();
    setTriageMarked(prev => {
      const next = new Map(prev);
      if (next.get(lemma) === "known") {
        next.delete(lemma);
      } else {
        next.set(lemma, "known");
      }
      return next;
    });
  }, []);

  const finishTriage = useCallback(async () => {
    if (!dbRef.current) return;
    const db = dbRef.current;
    const now = Date.now();

    const remaining: string[] = [];
    for (const lemma of triageLemmas) {
      const mark = triageMarked.get(lemma);
      if (mark === "good") {
        await reviewWord(db, lemma, Rating.Good, now, sourceLang);
      } else if (mark === "known") {
        await markKnown(db, lemma, sourceLang);
      } else {
        remaining.push(lemma);
      }
    }

    if (remaining.length === 0) {
      setPhase("done");
      setTotalCards(triageLemmas.length);
      setReviewed(triageLemmas.length);
      return;
    }

    setQueue(remaining.map(lemma => ({ lemma, flipped: false })));
    setTotalCards(remaining.length);
    setReviewed(0);
    setPhase("studying");
  }, [triageLemmas, triageMarked, sourceLang]);

  const flipCard = useCallback(() => {
    setQueue(q => {
      if (q.length === 0) return q;
      const next = [...q];
      next[0] = { ...next[0]!, flipped: true };
      return next;
    });
  }, []);

  const gradeCard = useCallback(async (grade: Grade) => {
    if (!dbRef.current || queue.length === 0) return;
    const card = queue[0]!;

    await reviewWord(dbRef.current, card.lemma, grade, Date.now(), sourceLang);

    const remaining = queue.slice(1);

    // Again/Hard → push to back for re-study
    if (grade === Rating.Again || grade === Rating.Hard) {
      remaining.push({ lemma: card.lemma, flipped: false });
    } else {
      setReviewed(r => r + 1);
    }

    if (remaining.length === 0) {
      setPhase("done");
    } else {
      setQueue(remaining);
    }
  }, [queue, sourceLang]);

  // Keyboard shortcuts: Space/Enter=flip, 1=Again, 2=Hard, 3=Good, 4=Easy
  useEffect(() => {
    if (phase !== "studying") return;
    const handler = (e: KeyboardEvent) => {
      if (queue.length === 0) return;
      const card = queue[0]!;
      if (!card.flipped) {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          flipCard();
        }
        return;
      }
      switch (e.key) {
        case "1": gradeCard(Rating.Again); break;
        case "2": gradeCard(Rating.Hard); break;
        case "3": gradeCard(Rating.Good); break;
        case "4": gradeCard(Rating.Easy); break;
        case " ":
        case "Enter":
          e.preventDefault();
          gradeCard(Rating.Good);
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, queue, flipCard, gradeCard]);

  const dismissCard = useCallback(async () => {
    if (!dbRef.current || queue.length === 0) return;
    const card = queue[0]!;
    await markKnown(dbRef.current, card.lemma, sourceLang);
    const remaining = queue.slice(1);
    setReviewed(r => r + 1);
    if (remaining.length === 0) {
      setPhase("done");
    } else {
      setQueue(remaining);
    }
  }, [queue, sourceLang]);

  const reset = useCallback(() => {
    setPhase("input");
    setQueue([]);
    setText("");
    setTriageLemmas([]);
    setTriageMarked(new Map());
    setTriageVisited(new Set());
  }, []);

  const [showInputTip, dismissInputTip] = useDismissible("input");
  const [showTriageTip, dismissTriageTip] = useDismissible("triage");
  const [showStudyTip, dismissStudyTip] = useDismissible("study");

  // --- Render ---

  if (error) {
    return (
      <div className="max-w-md mx-auto p-6 min-h-screen flex items-center justify-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="max-w-md mx-auto p-6 min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-2xl font-bold">
          {totalCards === 0 ? t("allKnown") : t("sessionComplete")}
        </p>
        <p className="text-neutral-400">
          {totalCards === 0
            ? t("noWordsNeedReview")
            : t("reviewedN", { n: totalCards, s: totalCards !== 1 ? "s" : "", eWord: totalCards !== 1 ? "e" : "a", eReviewed: totalCards !== 1 ? "e" : "a" })}
        </p>
        <button
          onClick={reset}
          className="mt-4 px-6 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600 transition-colors"
        >
          {t("newText")}
        </button>
      </div>
    );
  }

  if (phase === "triage") {
    const totalPages = Math.max(1, Math.ceil(triageLemmas.length / TRIAGE_BATCH_SIZE));
    const baseSize = Math.floor(triageLemmas.length / totalPages);
    const remainder = triageLemmas.length % totalPages;
    // First `remainder` pages get baseSize+1 items, the rest get baseSize
    const pageStart = triagePage < remainder
      ? triagePage * (baseSize + 1)
      : remainder * (baseSize + 1) + (triagePage - remainder) * baseSize;
    const pageSize = triagePage < remainder ? baseSize + 1 : baseSize;
    const pageLemmas = triageLemmas.slice(pageStart, pageStart + pageSize);
    const markedCount = triageMarked.size;
    const unmarkedCount = triageLemmas.length - markedCount;
    const allPagesVisited = triageVisited.size >= totalPages;

    return (
      <div className="max-w-md mx-auto p-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-bold">{t("quickScan")}</h2>
          <p className="text-sm text-neutral-400">
            {t("triageStatus", { marked: markedCount, unmarked: unmarkedCount })}
          </p>
        </div>
        <p className="text-xs text-neutral-500 mb-4">
          {t("triageHint")}
        </p>

        {showTriageTip && (
          <Tip onDismiss={dismissTriageTip}>
            <p className="font-medium text-neutral-200 mb-2 not-italic">{t("triageTipTitle")}</p>
            <p className="mb-2">{t("triageTipBody")}</p>
            <ul className="list-none space-y-1 mb-2">
              <li>{t("triageTipTap")}</li>
              <li>{t("triageTipRightClick")}</li>
            </ul>
            <p>{t("triageTipFooter")}</p>
          </Tip>
        )}

        <div className="flex flex-wrap justify-center gap-2">
          {pageLemmas.map(lemma => {
            const mark = triageMarked.get(lemma);
            return (
              <button
                key={lemma}
                onClick={() => triageClick(lemma)}
                onContextMenu={(e) => {
                  if (longPressFired.current) { e.preventDefault(); return; }
                  triageRightClick(lemma, e);
                }}
                onTouchStart={() => {
                  longPressFired.current = false;
                  longPressTimer.current = setTimeout(() => {
                    longPressFired.current = true;
                    triageRightClick(lemma, { preventDefault: () => {} } as React.MouseEvent);
                  }, 300);
                }}
                onTouchEnd={(e) => {
                  if (longPressTimer.current) clearTimeout(longPressTimer.current);
                  if (longPressFired.current) e.preventDefault();
                }}
                onTouchMove={() => {
                  if (longPressTimer.current) clearTimeout(longPressTimer.current);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors select-none ${
                  mark === "good"
                    ? "bg-green-900/60 border border-green-700 text-green-300"
                    : mark === "known"
                    ? "bg-neutral-600 border border-neutral-500 text-neutral-400 line-through"
                    : "bg-neutral-800 border border-neutral-700 hover:bg-neutral-700"
                }`}
              >
                {lemma}
              </button>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 pt-4 border-t border-neutral-700 flex items-center justify-center gap-3">
            {triagePage > 0 && (
              <button
                onClick={() => {
                  const prev = triagePage - 1;
                  setTriagePage(prev);
                  setTriageVisited(v => new Set(v).add(prev));
                }}
                className="text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
              >
                {t("prev")}
              </button>
            )}
            <span className="text-sm text-neutral-500">
              {triagePage + 1}/{totalPages}
            </span>
            <button
              onClick={() => {
                const next = Math.min(totalPages - 1, triagePage + 1);
                setTriagePage(next);
                setTriageVisited(v => new Set(v).add(next));
              }}
              disabled={triagePage === totalPages - 1}
              className="text-sm text-neutral-400 hover:text-neutral-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              {t("next")}
            </button>
          </div>
        )}
        {allPagesVisited && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={finishTriage}
              className="px-5 py-2 rounded-lg bg-green-800 hover:bg-green-700 transition-colors font-medium text-sm"
            >
              {unmarkedCount === 0 ? t("finish") : t("studyN", { n: unmarkedCount, s: unmarkedCount !== 1 ? "s" : "", eWord: unmarkedCount !== 1 ? "e" : "a" })}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (phase === "studying" && queue.length > 0) {
    const card = queue[0]!;
    return (
      <div className="max-w-md mx-auto p-6 min-h-screen flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <p className="text-sm text-neutral-400">
            {reviewed}/{totalCards}
          </p>
          <p className="text-sm text-neutral-400">
            {t("remaining", { n: queue.length, eRemaining: queue.length !== 1 ? "i" : "e" })}
          </p>
        </div>

        {showStudyTip && (
          <Tip onDismiss={dismissStudyTip}>
            <p className="font-medium text-neutral-200 mb-2 not-italic">{t("studyTipTitle")}</p>
            <p className="mb-2">{t("studyTipBody")}</p>
            <ul className="list-none space-y-1 mb-2">
              <li><span className="text-red-400 font-medium">{t("again")}</span> — {t("studyTipAgain").split(" — ")[1]}</li>
              <li><span className="text-orange-400 font-medium">{t("hard")}</span> — {t("studyTipHard").split(" — ")[1]}</li>
              <li><span className="text-green-400 font-medium">{t("good")}</span> — {t("studyTipGood").split(" — ")[1]}</li>
              <li><span className="text-blue-400 font-medium">{t("easy")}</span> — {t("studyTipEasy").split(" — ")[1]}</li>
            </ul>
            <p className="text-xs text-neutral-400">{t("studyTipKeyboard")}</p>
          </Tip>
        )}

        <div
          className={`flex-1 flex ${card.flipped ? "items-start overflow-y-auto" : "items-center"} justify-center cursor-pointer select-none min-h-0`}
          onClick={!card.flipped ? flipCard : undefined}
        >
          <div className="w-full max-w-sm">
            <div className="rounded-xl bg-neutral-800 border border-neutral-700 p-8 text-center min-h-48 flex flex-col items-center justify-center">
              <p className="text-3xl font-bold">{card.lemma}</p>
              {card.flipped && (() => {
                const def = getDefinitions(card.lemma);
                if (!def) {
                  return <p className="mt-4 text-neutral-400 text-lg italic">{t("noDefinition")}</p>;
                }
                return (
                  <p className="mt-4 text-neutral-300 text-lg">{def}</p>
                );
              })()}
              {!card.flipped && (
                <p className="mt-4 text-neutral-500 text-sm">{t("tapToReveal")}</p>
              )}
            </div>
          </div>
        </div>

        {card.flipped && (
          <div className="flex justify-center mt-4">
            <button
              onClick={dismissCard}
              className="px-4 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700/50 transition-colors"
            >
              {t("dismissWord")}
            </button>
          </div>
        )}

        {card.flipped && (
          <div className="grid grid-cols-4 gap-2 mt-2 pb-4">
            <button
              onClick={() => gradeCard(Rating.Again)}
              className="py-3 rounded-lg bg-red-900/60 hover:bg-red-900 border border-red-800 transition-colors text-sm font-medium"
            >
              <span className="opacity-40 text-xs">1</span> {t("again")}
            </button>
            <button
              onClick={() => gradeCard(Rating.Hard)}
              className="py-3 rounded-lg bg-orange-900/60 hover:bg-orange-900 border border-orange-800 transition-colors text-sm font-medium"
            >
              <span className="opacity-40 text-xs">2</span> {t("hard")}
            </button>
            <button
              onClick={() => gradeCard(Rating.Good)}
              className="py-3 rounded-lg bg-green-900/60 hover:bg-green-900 border border-green-800 transition-colors text-sm font-medium"
            >
              <span className="opacity-40 text-xs">3</span> {t("good")}
            </button>
            <button
              onClick={() => gradeCard(Rating.Easy)}
              className="py-3 rounded-lg bg-blue-900/60 hover:bg-blue-900 border border-blue-800 transition-colors text-sm font-medium"
            >
              <span className="opacity-40 text-xs">4</span> {t("easy")}
            </button>
          </div>
        )}
      </div>
    );
  }

  // phase === "input"
  return (
    <div className="max-w-md mx-auto p-6 min-h-screen flex flex-col">
      <div className="flex justify-between items-center mb-1">
        <h1 className="text-3xl font-bold">{t("appTitle")}</h1>
        <HamburgerMenu langPair={langPair} onLangPairChange={changeLangPair} />
      </div>
      <p className="text-sm text-neutral-400 mb-6">{t("subtitle")}</p>

      {showInputTip && (
        <Tip onDismiss={dismissInputTip}>
          <p className="font-medium text-neutral-200 mb-2 not-italic">{t("inputTipTitle")}</p>
          <p className="mb-2">{t("inputTipBody1")}</p>
          <p>{t("inputTipBody2")}</p>
        </Tip>
      )}

      <textarea
        className="w-full h-40 p-3 rounded-lg bg-neutral-800 border border-neutral-700 text-white resize-y focus:outline-none focus:border-neutral-500"
        placeholder={t(langPair.placeholderKey as any)}
        value={text}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
        autoFocus
      />

      <button
        onClick={startSession}
        disabled={!text.trim() || loading}
        className="mt-4 w-full py-3 rounded-lg bg-neutral-700 hover:bg-neutral-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
      >
        {loading && text.trim() && <span className="inline-block w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />}
        {t("study")}
      </button>
    </div>
  );
}

export default App;
